<template>
  <aside v-if="items.length" class="cg-toast-stack" aria-live="polite" aria-atomic="false">
    <article
      v-for="item in items"
      :key="item.id"
      class="cg-toast"
      :class="[`cg-toast--${item.tone}`, { 'cg-toast--loading': item.state === 'loading' }]"
      role="status"
    >
      <div class="cg-toast__icon" aria-hidden="true">
        <Icon v-if="item.state === 'loading'" name="loading-line" :size="16" class="cg-toast__spinner" />
        <span v-else>✓</span>
      </div>

      <div class="cg-toast__body">
        <p class="cg-toast__title">{{ item.title }}</p>
        <p v-if="item.description" class="cg-toast__description">{{ item.description }}</p>
      </div>

      <button class="cg-toast__close" type="button" aria-label="Закрыть уведомление" @click="toast.dismiss(item.id)">×</button>

      <div v-if="item.state === 'loading'" class="cg-toast__progress-track" aria-hidden="true">
        <span class="cg-toast__progress-fill cg-toast-indeterminate-bar" />
      </div>
    </article>
  </aside>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useToastStore } from "@/stores/toast";
import Icon from "@/components/ui/Icon.vue";

const toast = useToastStore();
const { items } = storeToRefs(toast);
</script>

<style scoped lang="scss">
.cg-toast-stack {
  position: fixed;
  top: 88px;
  right: 18px;
  z-index: 12050;
  width: min(360px, calc(100vw - 24px));
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.cg-toast {
  position: relative;
  pointer-events: auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: start;
  border-radius: 12px;
  border: 1px solid color-mix(in oklch, var(--color-success) 34%, var(--border));
  background: #000;
  box-shadow: 0 12px 24px color-mix(in oklch, var(--color-base-black) 36%, transparent);
  color: var(--on-overlay-text);
  overflow: hidden;
  padding: 12px 12px 14px;
}

.cg-toast--neutral {
  border-color: color-mix(in oklch, var(--ring) 30%, var(--border));
}

.cg-toast--error {
  border-color: color-mix(in oklch, var(--color-error) 55%, var(--border));
}

.cg-toast__icon {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  margin-top: 3px;
  background: color-mix(in oklch, var(--color-success) 75%, var(--color-base-black));
  color: #081308;
  font-size: 11px;
  font-weight: 800;
}

.cg-toast--error .cg-toast__icon {
  background: color-mix(in oklch, var(--color-error) 80%, var(--color-base-black));
  color: #210909;
}

.cg-toast--neutral .cg-toast__icon {
  background: color-mix(in oklch, var(--ring) 74%, var(--color-base-black));
  color: #0b0b0f;
}

.cg-toast__spinner {
  animation: cg-toast-spin 0.9s linear infinite;
  color: var(--color-brand-primary);
}

.cg-toast--loading .cg-toast__icon {
  background: transparent;
  color: var(--color-brand-primary);
}

.cg-toast--loading.cg-toast--neutral .cg-toast__icon,
.cg-toast--loading.cg-toast--error .cg-toast__icon {
  background: transparent;
  color: var(--color-brand-primary);
}

.cg-toast__body {
  min-width: 0;
}

.cg-toast__title {
  margin: 0;
  font-size: 13px;
  line-height: 1.3;
  font-weight: 700;
}

.cg-toast__description {
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: color-mix(in oklch, var(--on-overlay-text) 82%, transparent);
}

.cg-toast__close {
  border: 0;
  background: transparent;
  color: color-mix(in oklch, var(--on-overlay-text) 70%, transparent);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.cg-toast__progress-track {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: color-mix(in oklch, var(--on-overlay-text) 10%, transparent);
  overflow: hidden;
}

.cg-toast__progress-fill {
  position: absolute;
  width: 34%;
  height: 100%;
  border-radius: 999px;
  background: color-mix(in oklch, var(--ring) 80%, #fff);
}

@media (max-width: 640px) {
  .cg-toast-stack {
    top: 72px;
    right: 12px;
  }
}

@keyframes cg-toast-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
